import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import { appointmentAPI, doctorAPI, navigatorAPI, patientAPI, walletAPI } from '../services/api';
import Icon from '../components/Icon';
import { displayPatientCode } from '../utils/patientCode';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS   = ['Su','Mo','Tu','We','Th','Fr','Sa'];

export default function BookAppointmentPage() {
  const navigate = useNavigate();
  const [params]  = useSearchParams();
  const patientId = params.get('patientId');

  // Patient search state
  const [patientQuery,    setPatientQuery]    = useState('');
  const [patientResults,  setPatientResults]  = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchLoading,   setSearchLoading]   = useState(false);
  const [showDropdown,    setShowDropdown]    = useState(false);
  const searchRef = useRef(null);

  // Form state
  const [doctors,    setDoctors]    = useState([]);
  // Sep 2026 — Navigator can also be booked as the appointment's provider
  // (not just doctors), same "Book Appointment" flow, toggled by providerType.
  // Navigator-provided visits are free (no visit price list exists for them,
  // same policy as the existing navigator-confirmed follow-up flow) — see
  // insufficientWalletBalance below, which only applies to the DOCTOR path.
  const [navigators, setNavigators] = useState([]);
  const [form,       setForm]       = useState({
    providerType: 'DOCTOR', doctorId: '', navigatorId: '', specialisation: '',
    appointmentType: 'Follow-up', mode: 'In-person', notes: '',
  });

  // Calendar state
  const [calYear,  setCalYear]  = useState(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [selDate,  setSelDate]  = useState(null);
  const [selSlot,  setSelSlot]  = useState(null);
  const [slots,    setSlots]    = useState([]);

  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');

  // 27 Jul 2026: new — navigator bookings are wallet-only (Deepu, 27 Jul
  // 2026). Shown so the navigator sees the patient's balance BEFORE
  // clicking "Confirm Booking", rather than only discovering it's
  // insufficient after submitting.
  const [walletBalanceMinorUnits, setWalletBalanceMinorUnits] = useState(null);
  useEffect(() => {
    if (!selectedPatient?.id) { setWalletBalanceMinorUnits(null); return; }
    walletAPI.getBalance(selectedPatient.id)
      .then(res => setWalletBalanceMinorUnits(res.data.balanceMinorUnits ?? 0))
      .catch(() => setWalletBalanceMinorUnits(null));
  }, [selectedPatient?.id]);

  // 27 Jul 2026: new — flat ₹250 today (AppointmentPricingService is the
  // one place to change if/when per-doctor/visit-type pricing exists —
  // same flag already used everywhere else in this codebase for this
  // value). Used ONLY for this page's own disabled-button pre-check —
  // the actual charge is always resolved server-side, never trusted from
  // here.
  const APPOINTMENT_FLAT_FEE_RUPEES = 250;
  // Navigator-provided visits are free (skips the wallet debit entirely,
  // server-side — see AppointmentService.createAppointmentByNavigatorWithWallet),
  // so the wallet gate only applies when booking a doctor.
  const insufficientWalletBalance =
    form.providerType === 'DOCTOR' &&
    walletBalanceMinorUnits !== null && (walletBalanceMinorUnits / 100) < APPOINTMENT_FLAT_FEE_RUPEES;

  // 27 Jul 2026: new — checked once on page load, not because this page can
  // do anything different if Razorpay IS enabled (that path isn't built —
  // see NavigatorPaymentConfig.java's Javadoc), but so the navigator sees a
  // clear "this isn't available yet" message immediately rather than
  // filling out the whole form and only finding out after clicking Confirm.
  const [razorpayEnabledForNavigator, setRazorpayEnabledForNavigator] = useState(false);
  useEffect(() => {
    walletAPI.getNavigatorConfig()
      .then(res => setRazorpayEnabledForNavigator(!!res.data.razorpayEnabled))
      .catch(() => setRazorpayEnabledForNavigator(false)); // fail safe — assume wallet-only if the check itself fails
  }, []);

  // Load doctors
  useEffect(() => {
    doctorAPI.list()
      .then(r => setDoctors(Array.isArray(r.data) ? r.data : []))
      .catch(() => setDoctors([]));
  }, []);

  // Load navigators — same assignable-staff list NavigatorReassignPanel.jsx
  // uses, small enough to fetch in full rather than search-backed.
  useEffect(() => {
    navigatorAPI.assignable()
      .then(r => setNavigators(Array.isArray(r.data) ? r.data : []))
      .catch(() => setNavigators([]));
  }, []);

  // If patientId passed via URL, load that patient directly
  useEffect(() => {
    if (patientId) {
      patientAPI.get(patientId)
        .then(r => setSelectedPatient(r.data))
        .catch(() => {});
    }
  }, [patientId]);

  // Patient search — debounced
  const searchPatients = useCallback(async (q) => {
    if (q.length < 2) { setPatientResults([]); setShowDropdown(false); return; }
    setSearchLoading(true);
    try {
      const res = await patientAPI.list({ search: q, size: 8 });
      const list = Array.isArray(res.data)
        ? res.data
        : (res.data?.patients || res.data?.content || []);
      setPatientResults(list);
      setShowDropdown(true);
    } catch {
      setPatientResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchPatients(patientQuery), 300);
    return () => clearTimeout(t);
  }, [patientQuery, searchPatients]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Load slots when both a doctor and a date are selected. doctorId used to
  // be optional on the backend — omitting it meant every slot came back
  // "available" regardless of real bookings, and this effect leaned on that
  // by firing on date-select alone, papering over the no-doctor-yet case
  // with a hardcoded DEFAULT_SLOTS fallback. Now that availability is real
  // per-doctor data (weekly hours + day-off/custom-hours overrides), that
  // fallback would also mask a genuinely fully-booked or blocked day by
  // showing fake "available" slots — so this now waits for a real doctor,
  // same guard PatientBookAppointmentPage.jsx already uses, and a truly
  // empty response means truly no slots, not "show something anyway".
  const selectedProviderId = form.providerType === 'NAVIGATOR' ? form.navigatorId : form.doctorId;
  useEffect(() => {
    if (!selDate || !selectedProviderId) { setSlots([]); return; }
    const dateStr = `${calYear}-${String(calMonth + 1).padStart(2,'0')}-${String(selDate).padStart(2,'0')}`;
    const request = form.providerType === 'NAVIGATOR'
      ? appointmentAPI.availableSlotsForNavigator(form.navigatorId, dateStr)
      : appointmentAPI.availableSlots(form.doctorId, dateStr);
    request
      .then(r => setSlots((r.data?.slots || []).map(s => (
        { time: s.formattedTime, available: s.available, startTime: s.startTime }))))
      .catch(() => setSlots([]));
  }, [form.providerType, form.doctorId, form.navigatorId, selDate, calYear, calMonth]); // eslint-disable-line

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const to24Hour = (timeStr) => {
    if (!timeStr) return null;
    if (/^\d{2}:\d{2}$/.test(timeStr)) return timeStr;
    const [time, period] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    if (period?.toUpperCase() === 'PM' && hours !== 12) hours += 12;
    if (period?.toUpperCase() === 'AM' && hours === 12) hours = 0;
    return `${String(hours).padStart(2,'0')}:${String(minutes).padStart(2,'0')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPatient) { setError('Please select a patient.'); return; }
    if (!selDate || !selSlot) { setError('Please select a date and time slot.'); return; }
    if (form.providerType === 'NAVIGATOR') {
      if (!form.navigatorId) { setError('Please select a navigator.'); return; }
    } else if (!form.doctorId) { setError('Please select a doctor.'); return; }
    // 27 Jul 2026: new — client-side pre-check, defense-in-depth alongside
    // the submit button's disabled state below.
    if (insufficientWalletBalance) {
      setError(
        `Insufficient wallet balance: have ₹${(walletBalanceMinorUnits / 100).toLocaleString('en-IN')}, ` +
        `need ₹${APPOINTMENT_FLAT_FEE_RUPEES}. Ask the patient to add money to their wallet.`
      );
      return;
    }
    setSaving(true); setError('');
    try {
      const dateStr = `${calYear}-${String(calMonth + 1).padStart(2,'0')}-${String(selDate).padStart(2,'0')}`;
      const slotTime = typeof selSlot === 'string' ? selSlot : selSlot?.time || selSlot;
      // 27 Jul 2026: was appointmentAPI.create() — unpaid, no gate at all.
      // Navigator bookings are now wallet-only (Deepu, 27 Jul 2026):
      // navigatorBook() debits the PATIENT's wallet server-side before the
      // appointment is created, and fails closed with a clear message
      // ("Insufficient wallet balance: have X, need Y") if the balance
      // doesn't cover it — surfaced below via the same err.response?.data
      // .message pattern already used for every other error on this page,
      // not a new bespoke error type.
      await appointmentAPI.navigatorBook({
        patientId:       selectedPatient.id,
        ...(form.providerType === 'NAVIGATOR'
          ? { navigatorId: form.navigatorId }
          : { doctorId: form.doctorId }),
        appointmentDate: dateStr,
        appointmentTime: to24Hour(slotTime),
        appointmentType: form.appointmentType,
        consultationMode: form.mode,
        reason:          form.notes,
      });
      navigate(patientId
        ? `/patients/${patientId}?tab=Appointments`
        : `/patients/${selectedPatient.id}?tab=Appointments`
      );
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to book appointment.');
      setSaving(false);
    }
  };

  // Calendar
  const firstDay    = new Date(calYear, calMonth, 1).getDay();
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const today       = new Date();
  const isCurrentMonth = calYear === today.getFullYear() && calMonth === today.getMonth();

  const prevMonth = () => {
    if (calMonth === 0) { setCalYear(y => y - 1); setCalMonth(11); }
    else setCalMonth(m => m - 1);
    setSelDate(null); setSelSlot(null);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalYear(y => y + 1); setCalMonth(0); }
    else setCalMonth(m => m + 1);
    setSelDate(null); setSelSlot(null);
  };

  const filteredDoctors = form.specialisation
    ? doctors.filter(d => d.specialty === form.specialisation || d.specialisation === form.specialisation)
    : doctors;

  const specialisations = [...new Set(doctors.map(d => d.specialty || d.specialisation).filter(Boolean))];

  return (
    <AppShell breadcrumbs={[
      { label: 'Patients', to: '/patients' },
      ...(selectedPatient ? [{ label: `${selectedPatient.firstName} ${selectedPatient.lastName}`, to: `/patients/${selectedPatient.id}` }] : []),
      { label: 'Book Appointment' },
    ]}>
      <span className="back-link fade-in-up"
        onClick={() => navigate(patientId ? `/patients/${patientId}` : '/appointments')}>
        ← Back
      </span>

      {/* 27 Jul 2026: new — see NavigatorPaymentConfig.java's Javadoc. This
          should never actually render today (property defaults false), but
          if it's ever flipped without the corresponding code being built,
          this is a much clearer failure mode than a confusing 400 after
          filling out the whole form. */}
      {razorpayEnabledForNavigator && (
        <div className="alert alert-error" style={{ marginBottom: 16 }}>
          <Icon name="alert" size={14} /> Razorpay-based navigator booking is enabled in configuration, but not yet built.
          Please contact engineering before attempting to book — wallet-only booking below
          may not behave as expected until this is resolved.
        </div>
      )}

      <div className="page-header fade-in-up">
        <div>
          <div className="page-title">Book Appointment</div>
          <div className="page-subtitle">
            {selectedPatient
              ? [`${selectedPatient.firstName} ${selectedPatient.lastName}`, displayPatientCode(selectedPatient.patientCode)].filter(Boolean).join(' — ')
              : 'Search for a patient to get started'}
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error"><Icon name="alert" size={14} /> {error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-split">

          {/* ── Left column ── */}
          <div>
            {/* Step 1 — Patient Search */}
            {/* fade-in-up applies a transform, which makes this section (and
                its sibling below) each their own stacking context — so the
                dropdown's z-index only wins locally unless this section is
                explicitly raised above "2. Doctor & Type". */}
            <div className="form-section fade-in-up delay-1" style={{ overflow: "visible", position: "relative", zIndex: 2 }}>
              <div className="form-section-title">1. Select Patient</div>

              {selectedPatient ? (
                /* Selected patient card */
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  background: 'var(--blue-bg)', borderRadius: 'var(--radius-md)',
                  padding: '14px 16px', border: '1.5px solid var(--brand-blue)',
                }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: '50%',
                    background: 'linear-gradient(135deg, var(--brand-blue), var(--brand-accent))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, fontWeight: 700, color: 'var(--text-inverse)', flexShrink: 0,
                  }}>
                    {selectedPatient.firstName?.[0]}{selectedPatient.lastName?.[0]}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>
                      {selectedPatient.firstName} {selectedPatient.lastName}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                      {[
                        displayPatientCode(selectedPatient.patientCode),
                        selectedPatient.phone,
                        selectedPatient.dateOfBirth && `Born ${new Date(selectedPatient.dateOfBirth).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}`,
                      ].filter(Boolean).join(' · ')}
                    </div>
                    {selectedPatient.medicalHistory && (
                      <div style={{ fontSize: 12, color: 'var(--brand-blue)', marginTop: 2, fontWeight: 500 }}>
                        {selectedPatient.medicalHistory}
                      </div>
                    )}
                    {/* 27 Jul 2026: new — navigator bookings are wallet-only,
                        so the balance is worth seeing up front rather than
                        only discovering it's short after clicking Confirm. */}
                    {walletBalanceMinorUnits !== null && (
                      <div style={{
                        fontSize: 12, marginTop: 4, fontWeight: 600,
                        color: walletBalanceMinorUnits > 0 ? 'var(--brand-blue)' : 'var(--red)',
                      }}>
                        Wallet balance: ₹{(walletBalanceMinorUnits / 100).toLocaleString('en-IN')}
                        {walletBalanceMinorUnits === 0 && form.providerType === 'DOCTOR' && ' — patient needs to top up before booking'}
                      </div>
                    )}
                  </div>
                  {!patientId && (
                    <button type="button" className="btn btn-ghost btn-sm"
                      onClick={() => { setSelectedPatient(null); setPatientQuery(''); }}>
                      ✕ Change
                    </button>
                  )}
                </div>
              ) : (
                /* Patient search input */
                <div ref={searchRef} style={{ position: 'relative' }}>
                  <div className="search-input-wrap" style={{ maxWidth: '100%' }}>
                    <span className="search-icon"><Icon name="search" size={15} /></span>
                    <input
                      className="search-input"
                      style={{ maxWidth: '100%', paddingRight: searchLoading ? 36 : 12 }}
                      placeholder="Search by name or phone number…"
                      value={patientQuery}
                      onChange={e => { setPatientQuery(e.target.value); setError(''); }}
                      autoFocus
                    />
                    {searchLoading && (
                      <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
                        <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                      </div>
                    )}
                  </div>

                  {/* Dropdown results */}
                  {showDropdown && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0,
                      background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)',
                      border: '1.5px solid var(--border)', boxShadow: 'var(--shadow-lg)',
                      zIndex: 9999, maxHeight: 280, overflowY: 'auto', marginTop: 4,
                    }}>
                      {patientResults.length === 0 ? (
                        <div style={{ padding: '16px 16px', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>
                          No patients found for "{patientQuery}"
                        </div>
                      ) : patientResults.map(p => (
                        <div key={p.id}
                          onClick={() => { setSelectedPatient(p); setShowDropdown(false); setPatientQuery(''); }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 12,
                            padding: '11px 16px', cursor: 'pointer',
                            borderBottom: '1px solid var(--bg-subtle)',
                            transition: 'background 0.1s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <div style={{
                            width: 36, height: 36, borderRadius: '50%',
                            background: 'var(--blue-bg)', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            fontSize: 13, fontWeight: 700, color: 'var(--brand-blue)', flexShrink: 0,
                          }}>
                            {p.firstName?.[0]}{p.lastName?.[0]}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>
                              {p.firstName} {p.lastName}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
                              {[displayPatientCode(p.patientCode), p.phone, p.medicalHistory].filter(Boolean).join(' · ')}
                            </div>
                          </div>
                          <span className={`badge badge-${p.status === 'ACTIVE' ? 'active' : 'pending'}`}
                            style={{ fontSize: 10 }}>
                            {p.status || 'Active'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {patientQuery.length > 0 && patientQuery.length < 2 && (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6, paddingLeft: 4 }}>
                      Type at least 2 characters to search
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Step 2 — Provider & Type */}
            <div className="form-section fade-in-up delay-2">
              <div className="form-section-title">2. Provider & Type</div>
              <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
                <div className="form-group">
                  <label className="form-label">Provider <span className="required">*</span></label>
                  <div className="tabs-bar" style={{ marginBottom: 0 }}>
                    <button type="button"
                      className={`tab-btn ${form.providerType === 'DOCTOR' ? 'active' : ''}`}
                      onClick={() => setForm(f => ({ ...f, providerType: 'DOCTOR', navigatorId: '' }))}>
                      Doctor
                    </button>
                    <button type="button"
                      className={`tab-btn ${form.providerType === 'NAVIGATOR' ? 'active' : ''}`}
                      onClick={() => setForm(f => ({ ...f, providerType: 'NAVIGATOR', doctorId: '', specialisation: '' }))}>
                      Navigator
                    </button>
                  </div>
                </div>

                {form.providerType === 'DOCTOR' ? (
                  <>
                    <div className="form-group">
                      <label className="form-label">Specialisation</label>
                      <select className="form-select" value={form.specialisation} onChange={set('specialisation')}>
                        <option value="">All Specialisations</option>
                        {specialisations.map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Doctor <span className="required">*</span></label>
                      <select className="form-select" value={form.doctorId} onChange={set('doctorId')} required>
                        <option value="">Select doctor</option>
                        {filteredDoctors.map(d => (
                          <option key={d.id} value={d.id}>
                            Dr. {d.firstName} {d.lastName}
                            {(d.specialty || d.specialisation) ? ` — ${d.specialty || d.specialisation}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                ) : (
                  <div className="form-group">
                    <label className="form-label">Navigator <span className="required">*</span></label>
                    <select className="form-select" value={form.navigatorId} onChange={set('navigatorId')} required>
                      <option value="">Select navigator</option>
                      {navigators.map(n => (
                        <option key={n.id} value={n.id}>{n.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Appointment Type <span className="required">*</span></label>
                  <select className="form-select" value={form.appointmentType} onChange={set('appointmentType')}>
                    {['Follow-up','New Consultation','Routine Check','Emergency'].map(t => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Consultation Mode</label>
                  <select className="form-select" value={form.mode} onChange={set('mode')}>
                    <option>In-person</option>
                    <option>Telemedicine</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Step 4 — Notes */}
            <div className="form-section fade-in-up delay-3">
              <div className="form-section-title">4. Notes</div>
              <textarea
                className="form-textarea"
                style={{ width: '100%' }}
                placeholder="Reason for visit, symptoms, special instructions…"
                value={form.notes}
                onChange={set('notes')}
              />
              <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
                <button className="btn btn-primary btn-lg btn-wrap" type="submit"
                  disabled={
                    saving || !selectedPatient || !selDate || !selSlot || !selectedProviderId ||
                    insufficientWalletBalance
                  }>
                  {saving
                    ? 'Booking…'
                    : insufficientWalletBalance
                      ? 'Insufficient Wallet Balance'
                      : form.providerType === 'NAVIGATOR'
                        ? 'Confirm Booking (free)'
                        : 'Confirm Booking (charged to patient wallet)'}
                </button>
                <button className="btn btn-ghost" type="button" onClick={() => navigate(-1)}>Cancel</button>
              </div>
              {/* 27 Jul 2026: new — insufficientWalletBalance check added
                  here (Deepu's UX feedback, 27 Jul 2026): this text sits
                  right next to the button, so it's visible even if the
                  navigator never scrolls up to the patient card. */}
              {(!selectedPatient || !selDate || !selSlot || !selectedProviderId) ? (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                  ℹ {!selectedPatient
                      ? 'Select a patient'
                      : !selectedProviderId
                        ? (form.providerType === 'NAVIGATOR' ? 'Select a navigator' : 'Select a doctor')
                        : 'Select a date and time slot'} to confirm
                </div>
              ) : insufficientWalletBalance ? (
                <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 8, fontWeight: 600 }}>
                  <Icon name="alert" size={14} /> Patient's wallet balance (₹{(walletBalanceMinorUnits / 100).toLocaleString('en-IN')}) is too low.
                  Ask them to top up first.
                </div>
              ) : null}
            </div>
          </div>

          {/* ── Right column — Calendar ── */}
          <div className="form-section fade-in-up delay-2" style={{ alignSelf: 'start' }}>
            <div className="form-section-title">3. Select Date & Time</div>

            <div className="calendar-header">
              <button type="button" className="btn btn-ghost btn-sm" onClick={prevMonth}>‹</button>
              <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>
                {MONTHS[calMonth]} {calYear}
              </span>
              <button type="button" className="btn btn-ghost btn-sm" onClick={nextMonth}>›</button>
            </div>

            <div className="calendar-grid" style={{ marginBottom: 4 }}>
              {DAYS.map(d => <div key={d} className="cal-day header">{d}</div>)}
              {Array.from({ length: firstDay }).map((_, i) => <div key={`e-${i}`} className="cal-day" />)}
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                const isPast    = isCurrentMonth && day < today.getDate();
                const isToday   = isCurrentMonth && day === today.getDate();
                const isSelected = selDate === day;
                const isSunday  = new Date(calYear, calMonth, day).getDay() === 0;
                return (
                  <div key={day}
                    className={`cal-day ${isPast || isSunday ? 'past' : isToday ? 'today' : isSelected ? 'selected' : 'available'}`}
                    style={{ opacity: isSunday ? 0.35 : 1 }}
                    onClick={() => { if (!isPast && !isSunday) { setSelDate(day); setSelSlot(null); } }}
                  >
                    {day}
                  </div>
                );
              })}
            </div>

            {selDate && !selectedProviderId && (
              <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: 13 }}>
                ☝ Select a {form.providerType === 'NAVIGATOR' ? 'navigator' : 'doctor'} to see their available slots
              </div>
            )}

            {selDate && selectedProviderId && (
              <div style={{ marginTop: 18 }}>
                <div className="form-label" style={{ marginBottom: 10 }}>
                  Available Slots — {selDate} {MONTHS[calMonth].slice(0,3)} (
                    {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][new Date(calYear, calMonth, selDate).getDay()]}
                  )
                </div>
                {slots.length === 0
                  ? <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '12px 0' }}>
                      No slots available for this date
                    </div>
                  : <div className="slot-grid">
                      {slots.map((slot, i) => {
                        const slotTime  = typeof slot === 'string' ? slot : slot.time;
                        const isTaken   = typeof slot === 'object' ? !slot.available : false;
                        const isSelSlot = selSlot === slotTime;
                        return (
                          <div key={i}
                            className={`slot ${isTaken ? 'taken' : isSelSlot ? 'selected' : 'available'}`}
                            onClick={() => !isTaken && setSelSlot(slotTime)}
                          >
                            {slotTime}{isSelSlot ? ' ✓' : ''}
                          </div>
                        );
                      })}
                    </div>
                }
                <div style={{ marginTop: 10, display: 'flex', gap: 14, fontSize: 12, color: 'var(--text-muted)' }}>
                  <span>Available</span>
                  <span style={{ opacity: 0.5 }}>⬜ Booked</span>
                  <span style={{ color: 'var(--brand-blue)' }}>✓ Selected</span>
                </div>
              </div>
            )}

            {!selDate && (
              <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: 13 }}>
                ☝ Select a date to see available slots
              </div>
            )}
          </div>
        </div>
      </form>
    </AppShell>
  );
}
